import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, ArrowClockwise, Image as ImageIcon, Trash, FloppyDisk, Star } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const DEFAULTS = {
  primary_color: "#EA580C",
  primary_text_color: "#FFFFFF",
  label_bg_color: "#F1F5F9",
  highlight_color: "#FFFF00",
  title_text_color: "#0F172A",
  logo_data_url: "",
};

const FIELDS = [
  { key: "primary_color", label: "Naranja de encabezados", hint: "Barras de secciones (Actividades, Detección, Ambiente…)" },
  { key: "primary_text_color", label: "Texto sobre encabezados", hint: "Color del texto blanco en las barras naranjas" },
  { key: "label_bg_color", label: "Fondo de etiquetas", hint: "Celdas gris claro (Fecha, DSAI, Personal, etc.)" },
  { key: "highlight_color", label: "Resaltado ACTO / CONDICIÓN", hint: "Marca amarilla en la incidencia detectada" },
  { key: "title_text_color", label: "Color del título del reporte", hint: "Solo aplica al PDF" },
];

// Paletas built-in (siempre presentes)
const BUILTIN_PRESETS = [
  {
    id: "silverco", name: "Silverco naranja", swatch: "#EA580C", builtin: true,
    colors: { primary_color: "#EA580C", primary_text_color: "#FFFFFF", label_bg_color: "#F1F5F9", highlight_color: "#FFFF00", title_text_color: "#0F172A" },
  },
  {
    id: "mafrisa", name: "Mafrisa azul", swatch: "#1E40AF", builtin: true,
    colors: { primary_color: "#1E40AF", primary_text_color: "#FFFFFF", label_bg_color: "#DBEAFE", highlight_color: "#FDE047", title_text_color: "#0F172A" },
  },
  {
    id: "corporativo", name: "Corporativo verde", swatch: "#059669", builtin: true,
    colors: { primary_color: "#059669", primary_text_color: "#FFFFFF", label_bg_color: "#D1FAE5", highlight_color: "#FEF08A", title_text_color: "#052E16" },
  },
];

function isHex(v) { return typeof v === "string" && /^#[0-9A-Fa-f]{6}$/.test(v); }

export default function DailyReportThemePanel({ canManage }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(DEFAULTS);
  const [customPresets, setCustomPresets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  const [creatingPreset, setCreatingPreset] = useState(false);

  const loadCustomPresets = async () => {
    try {
      const { data } = await api.get("/settings/daily-report-theme-presets");
      setCustomPresets(data?.presets || []);
    } catch {
      setCustomPresets([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/settings/daily-report-theme")
      .then((r) => setTheme({ ...DEFAULTS, ...(r.data || {}) }))
      .catch(() => setTheme(DEFAULTS));
    loadCustomPresets();
  }, [open]);

  const applyPreset = (preset) => {
    setTheme((t) => ({ ...t, ...preset.colors }));
    toast.success(`Preset "${preset.name}" aplicado — recuerda guardar`);
  };

  const saveNewPreset = async () => {
    const name = (newPresetName || "").trim();
    if (!name) { toast.error("Nombre del preset requerido"); return; }
    for (const { key, label } of FIELDS) {
      if (!isHex(theme[key])) {
        toast.error(`"${label}" debe ser hex #RRGGBB para guardar como preset`);
        return;
      }
    }
    setCreatingPreset(true);
    try {
      const payload = {
        name,
        primary_color: theme.primary_color,
        primary_text_color: theme.primary_text_color,
        label_bg_color: theme.label_bg_color,
        highlight_color: theme.highlight_color,
        title_text_color: theme.title_text_color,
      };
      await api.post("/settings/daily-report-theme-presets", payload);
      toast.success(`Preset "${name}" guardado`);
      setNewPresetName("");
      loadCustomPresets();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreatingPreset(false);
    }
  };

  const deleteCustomPreset = async (id, name) => {
    if (!window.confirm(`¿Eliminar preset "${name}"?`)) return;
    try {
      await api.delete(`/settings/daily-report-theme-presets/${id}`);
      toast.success(`Preset "${name}" eliminado`);
      loadCustomPresets();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handleLogoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Debe ser una imagen"); return; }
    if (file.size > 500_000) { toast.error(`Imagen ${Math.round(file.size/1024)}KB · máx 500KB`); return; }
    setUploadingLogo(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result); r.onerror = reject;
        r.readAsDataURL(file);
      });
      setTheme((t) => ({ ...t, logo_data_url: dataUrl }));
      toast.success("Logo cargado — recuerda guardar");
    } catch { toast.error("Error leyendo la imagen"); }
    finally { setUploadingLogo(false); }
  };

  const save = async () => {
    for (const { key, label } of FIELDS) {
      if (!isHex(theme[key])) { toast.error(`"${label}" debe ser hex #RRGGBB`); return; }
    }
    setSaving(true);
    try {
      await api.put("/settings/daily-report-theme", theme);
      toast.success("Configuración guardada — se aplica al próximo PDF/XLSX");
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => setTheme(DEFAULTS);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="daily-theme-button" variant="outline"
          className="gap-1 h-9 bg-fuchsia-50 border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100">
          <Palette size={14} weight="bold" /> Colores y logo
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="daily-theme-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Palette weight="bold" /> Personalizar Reporte Diario HSE
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-slate-500 -mt-2">
          Colores + logo del cliente se aplican al PDF y XLSX. Cambios efectivos en la próxima descarga.
        </p>

        {/* Presets Built-in + Custom */}
        <div className="border border-slate-200 rounded p-3 bg-slate-50/40 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-bold">Presets built-in</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {BUILTIN_PRESETS.map((p) => (
                <button key={p.id} data-testid={`daily-theme-preset-${p.id}`} onClick={() => applyPreset(p)}
                  disabled={!canManage}
                  className="flex items-center gap-2 rounded border border-slate-300 hover:border-slate-500 bg-white p-2 transition-colors disabled:opacity-50">
                  <span className="w-6 h-6 rounded shadow-inner border border-slate-200" style={{ backgroundColor: p.swatch }} />
                  <span className="text-xs font-semibold text-slate-700 text-left flex-1">{p.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-bold flex items-center gap-2">
              <Star size={12} weight="fill" className="text-amber-500" /> Mis presets guardados
              {customPresets.length > 0 && <span className="text-slate-400 font-normal normal-case">({customPresets.length})</span>}
            </Label>
            {customPresets.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic mt-1">Aún no has guardado ninguno. Elige los colores y usa &quot;Guardar como preset&quot; abajo.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5" data-testid="daily-theme-custom-presets-list">
                {customPresets.map((p) => (
                  <div key={p.id} data-testid={`daily-theme-custom-${p.id}`}
                    className="flex items-center gap-2 rounded border border-amber-200 hover:border-amber-400 bg-amber-50 p-2 transition-colors">
                    <button
                      onClick={() => applyPreset({ name: p.name, colors: p })}
                      disabled={!canManage}
                      className="flex items-center gap-2 flex-1 disabled:opacity-50"
                      data-testid={`daily-theme-custom-apply-${p.id}`}
                    >
                      <span className="w-5 h-5 rounded shadow-inner border border-slate-200" style={{ backgroundColor: p.primary_color }} />
                      <span className="w-5 h-5 rounded shadow-inner border border-slate-200 -ml-2" style={{ backgroundColor: p.highlight_color }} />
                      <span className="text-xs font-semibold text-slate-800 text-left flex-1 truncate">{p.name}</span>
                    </button>
                    {canManage && (
                      <button
                        data-testid={`daily-theme-custom-delete-${p.id}`}
                        onClick={() => deleteCustomPreset(p.id, p.name)}
                        className="text-red-500 hover:text-red-800"
                        title="Eliminar preset"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {canManage && (
            <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
              <Input
                data-testid="daily-theme-preset-name"
                placeholder="Nombre del nuevo preset (ej: Cliente X)"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                maxLength={60}
                className="h-8 text-sm"
              />
              <Button
                data-testid="daily-theme-preset-create"
                size="sm"
                onClick={saveNewPreset}
                disabled={creatingPreset || !newPresetName.trim()}
                className="gap-1 h-8 bg-amber-600 hover:bg-amber-700"
              >
                <FloppyDisk size={12} weight="bold" /> {creatingPreset ? "…" : "Guardar como preset"}
              </Button>
            </div>
          )}
        </div>

        {/* Logo */}
        <div className="border border-slate-200 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <ImageIcon weight="bold" /> Logo del cliente
              </Label>
              <p className="text-[11px] text-slate-500">Se incrusta en la cabecera del PDF y XLSX · máx 500KB</p>
            </div>
            {theme.logo_data_url && (
              <button data-testid="daily-theme-logo-remove"
                onClick={() => setTheme((t) => ({ ...t, logo_data_url: "" }))}
                disabled={!canManage}
                className="text-red-600 hover:text-red-800 disabled:opacity-40" title="Quitar logo">
                <Trash size={16} weight="bold" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 h-16 border border-dashed border-slate-300 rounded flex items-center justify-center bg-slate-50 overflow-hidden">
              {theme.logo_data_url ? (
                <img src={theme.logo_data_url} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="daily-theme-logo-preview" />
              ) : (
                <span className="text-[10px] text-slate-400 italic">Sin logo</span>
              )}
            </div>
            <Input type="file" accept="image/*" data-testid="daily-theme-logo-file"
              disabled={!canManage || uploadingLogo}
              onChange={(e) => handleLogoFile(e.target.files?.[0])}
              className="flex-1 file:bg-fuchsia-100 file:text-fuchsia-800 file:border-0 file:mr-3 file:px-3 file:py-1 file:rounded" />
          </div>
        </div>

        {/* Colores */}
        <div className="space-y-3">
          {FIELDS.map(({ key, label, hint }) => (
            <div key={key} className="grid grid-cols-[1fr,80px,120px] gap-3 items-center border border-slate-200 rounded p-2.5">
              <div>
                <Label className="text-sm font-semibold text-slate-800">{label}</Label>
                <p className="text-[11px] text-slate-500 leading-snug">{hint}</p>
              </div>
              <input type="color" data-testid={`daily-theme-${key}-picker`}
                value={theme[key] || "#000000"} disabled={!canManage}
                onChange={(e) => setTheme({ ...theme, [key]: e.target.value.toUpperCase() })}
                className="w-full h-9 rounded cursor-pointer disabled:opacity-50" />
              <Input data-testid={`daily-theme-${key}-hex`} value={theme[key] || ""}
                disabled={!canManage}
                onChange={(e) => setTheme({ ...theme, [key]: e.target.value.toUpperCase() })}
                placeholder="#RRGGBB" maxLength={7}
                className="font-mono text-sm h-9" />
            </div>
          ))}
        </div>

        {/* Preview */}
        <div className="border border-slate-300 rounded overflow-hidden">
          <div className="text-xs uppercase tracking-wider bg-slate-100 px-3 py-1 text-slate-600 font-semibold">Vista previa</div>
          <div className="p-3 space-y-2" style={{ color: theme.title_text_color || "#0F172A" }}>
            <div className="flex items-center gap-3">
              {theme.logo_data_url && <img src={theme.logo_data_url} alt="" className="h-8 max-w-[80px] object-contain" />}
              <div className="text-center font-bold text-base flex-1">Reporte Diario de Seguridad, Salud y Medio Ambiente</div>
            </div>
            <div className="text-center py-1 font-bold text-sm" style={{ backgroundColor: theme.primary_color, color: theme.primary_text_color }}>
              DETECCIÓN DE ACTOS Y CONDICIONES INSEGURAS
            </div>
            <div className="grid grid-cols-6 gap-0 text-xs border">
              <div className="px-2 py-1 col-span-3 border-r">1.- Extintor bloqueado</div>
              <div className="px-2 py-1 text-center border-r font-bold" style={{ backgroundColor: theme.highlight_color }}>ACTO</div>
              <div className="px-2 py-1 text-center border-r font-bold">CONDICIÓN</div>
              <div className="px-2 py-1 text-center">Taller</div>
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-2 pt-2">
          <Button data-testid="daily-theme-reset" variant="outline" onClick={resetDefaults}
            disabled={!canManage || saving} className="gap-1">
            <ArrowClockwise size={14} weight="bold" /> Restaurar Silverco
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
            <Button data-testid="daily-theme-save" onClick={save} disabled={!canManage || saving}
              className="bg-orange-700 hover:bg-orange-800">
              {saving ? "Guardando…" : "Guardar todo"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
